import {
  Row,
  Column,
  ButtonGroup,
  useToast,
  Dialog,
  Button,
  RefreshIcon,
  Heading,
} from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import { isEqual, omit, omitBy } from "lodash";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef, useState } from "react";

import { PermissionedButton } from "src/components/permission";
import { SourceForm, type SourceFormRef } from "src/components/sources/setup";
import {
  DEFAULT_LIGHTNING_SCHEMA_CONFIG,
  type WarehouseSchemaConfig,
} from "src/components/sources/setup/constants";
import { useSourceTesting } from "src/components/sources/testing/hooks";
import {
  type ListSourceTestStepsQueryVariables,
  useExternalBucketsQuery,
  useSourceQuery,
  useUpdateSourceV2Mutation,
} from "src/graphql";
import type { SourceConfig } from "src/utils/sources";
import { Testing } from "src/components/sources/testing";
import { useParams } from "src/router";
import { DocsLink } from "src/components/docs-link";
import { FloatingActionBar } from "src/components/action-bar";
import { ZeroCopySharing } from "./zero-copy-sharing";
import { SourceWarehouseExportReEnable } from "src/pages/sources/source/warehouse-export-reenable";

export const SourceConfiguration = () => {
  const { id } = useParams<{ id: string }>();
  const { data: source } = useSourceQuery(
    { id: String(id) },
    { select: (data) => data.connections_by_pk, suspense: true },
  );
  const { toast } = useToast();
  const { data: externalBucketsData } = useExternalBucketsQuery(undefined, {
    suspense: false,
  });
  const { wheFirstClassSurfacing } = useFlags();
  // Offer the external-stage config only for sources whose exporter supports a
  // named stage, and only when the workspace exports to its own bucket (the
  // stage points at that bucket), so gate on a configured external bucket.
  // Shares the surfacing flag because the classified reason for a blocked
  // inline unload tells the customer to create a named stage: showing that
  // message without this field would leave them nowhere to act on it.
  const showWarehouseExportStage =
    Boolean(wheFirstClassSurfacing) &&
    Boolean(source?.definition?.supportsWarehouseExportStage) &&
    (externalBucketsData?.external_buckets?.length ?? 0) > 0;
  const [config, setConfig] = useState<SourceConfig | undefined>();
  const [tunnelId, setTunnelId] = useState<string | null>();
  const [credentialId, setCredentialId] = useState<string | undefined>();
  const [lightningEnabled, setLightningEnabled] = useState<
    boolean | undefined
  >();
  const [plannerDatabase, setPlannerDatabase] = useState<string | undefined>();
  const [schema, setSchema] = useState<WarehouseSchemaConfig>(
    DEFAULT_LIGHTNING_SCHEMA_CONFIG,
  );
  const [testConnectionModalOpen, setTestConnectionModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [lightningSchemaMode, setLightningSchemaMode] = useState<
    "shared" | "separate" | undefined
  >();
  const sourceFormRef = useRef<SourceFormRef>(null);
  const { isLoading: updateLoading, mutateAsync: updateSource } =
    useUpdateSourceV2Mutation();

  const {
    results: testResults,
    steps: testSteps,
    getTestSteps,
    runTest,
    cancelRunTest,
    timeElapsed,
  } = useSourceTesting();

  const configChanged = !isEqual(
    config
      ? omit(
          omitBy(config, (v) => v === undefined),
          ["methodKey"],
        )
      : undefined,
    omit(source!.config, ["methodKey"]) ?? undefined,
  );
  const tunnelChanged = !isEqual(tunnelId, source!.tunnel?.id ?? undefined);
  const credentialChanged = !isEqual(
    credentialId,
    source!.credential_id ?? undefined,
  );
  const lightningEnabledChanged = !isEqual(
    lightningEnabled,
    source!.plan_in_warehouse ?? undefined,
  );
  const plannerDatabaseChanged = !isEqual(
    plannerDatabase,
    source!.plan_in_warehouse_config?.plannerDatabase ?? undefined,
  );
  const schemaConfigChanged = !isEqual(
    schema,
    source!.plan_in_warehouse_config?.schema ?? undefined,
  );

  const dirty =
    tunnelChanged ||
    configChanged ||
    credentialChanged ||
    lightningEnabledChanged ||
    plannerDatabaseChanged ||
    schemaConfigChanged;
  const complete = tunnelId !== null;

  const variables: ListSourceTestStepsQueryVariables = {
    sourceType: source!.definition?.type,
    sourceId: String(source!.id),
    configuration: config,
    credentialId: credentialId ? Number(credentialId) : undefined,
    tunnelId: tunnelId ? String(tunnelId) : undefined,
    warehousePlanConfig: lightningEnabled
      ? { plannerDatabase, schema }
      : undefined,
  };

  const save = async () => {
    setIsSaving(true);
    let updatedConfig = config;

    if (tunnelId) {
      updatedConfig = {
        ...updatedConfig,
        host: null,
        server: null,
        port: null,
      };
    }
    try {
      const isValid = await sourceFormRef.current?.validate(
        updatedConfig ?? {},
        {
          sourceDefinition: source!.definition,
          sourceId: source!.id,
          isSetup: true,
          tunnel: tunnelId,
          credentialId,
        },
      );
      if (!isValid) {
        toast({
          id: "update-source",
          title: "There is an error in your source configuration",
          message: "Please fix the errors and try again",
          variant: "error",
        });
        setIsSaving(false);
        return;
      }

      await updateSource({
        id: source!.id.toString(),
        source: {
          tunnel_id: tunnelId ? tunnelId : null,
          credential_id:
            credentialId != null ? String(credentialId) : undefined,
          config: updatedConfig,
          plan_in_warehouse: lightningEnabled || undefined,
          plan_in_warehouse_config: lightningEnabled
            ? { plannerDatabase, schema }
            : undefined,
        },
      });
    } catch (error) {
      captureException(error);
      toast({
        id: "update-source",
        title: "Couldn't update source",
        message: error.message,
        variant: "error",
      });
    }

    setIsSaving(false);
  };

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!testConnectionModalOpen) cancelRunTest();
  }, [testConnectionModalOpen]);

  useEffect(() => {
    setConfig(source?.config ?? undefined);
    setTunnelId(source?.tunnel?.id ?? undefined);
    setCredentialId(source?.credential_id ?? undefined);
    setLightningEnabled(source?.plan_in_warehouse ?? undefined);
    setPlannerDatabase(
      source?.plan_in_warehouse_config?.plannerDatabase ?? undefined,
    );

    const schema =
      source?.plan_in_warehouse_config?.schema ??
      DEFAULT_LIGHTNING_SCHEMA_CONFIG;
    const separateSchema = new Set(Object.values(schema)).size > 1;
    setSchema(schema);
    setLightningSchemaMode(separateSchema ? "separate" : "shared");
  }, [source]);

  return (
    <>
      <Column gap={6} flexGrow={1} minWidth={0}>
        <Row align="center" justify="space-between">
          <Heading>{source!.definition?.name} configuration</Heading>
          <Row align="center" gap={4} overflow="hidden">
            <DocsLink
              href={source!.definition?.docs ?? ""}
              name={source!.definition?.name ?? ""}
            />
            {!source!.definition?.disableTest && config && (
              <PermissionedButton
                permission={{
                  v2: {
                    resource: "source",
                    grant: "can_update",
                    id: source!.id,
                  },
                }}
                isDisabled={!complete}
                onClick={async () => {
                  await getTestSteps(variables);
                  runTest(variables);
                  setTestConnectionModalOpen(true);
                }}
              >
                Test connection
              </PermissionedButton>
            )}
          </Row>
        </Row>
        <SourceWarehouseExportReEnable
          sourceId={Number(source!.id)}
          supportsWarehouseExport={Boolean(
            source!.definition?.supportsWarehouseExport,
          )}
        />
        {source!.definition && (
          <SourceForm
            ref={sourceFormRef}
            //Cheeky way to reset child componenents to initial state after save. (EX: Reseting senstive field edit state)
            key={`${updateLoading}`}
            hideSecret
            config={config}
            credentialId={credentialId}
            definition={source!.definition}
            hasSetupLightning={Boolean(source!.plan_in_warehouse)}
            isSetup={false}
            lightningEnabled={lightningEnabled}
            plannerDatabase={plannerDatabase}
            setConfig={setConfig}
            setCredentialId={setCredentialId}
            setLightningEnabled={setLightningEnabled}
            setPlannerDatabase={setPlannerDatabase}
            setLightningSchemaMode={setLightningSchemaMode}
            schema={schema}
            setSchema={setSchema}
            lightningSchemaMode={lightningSchemaMode}
            warehouseExportStage={
              typeof config?.stage === "string" ? config.stage : undefined
            }
            setWarehouseExportStage={(value) =>
              setConfig((prev) => ({ ...prev, stage: value || undefined }))
            }
            showWarehouseExportStage={showWarehouseExportStage}
            setTunnelId={setTunnelId}
            sourceId={id}
            tunnelId={tunnelId}
          />
        )}
        {source?.definition?.type && (
          <ZeroCopySharing
            key={source.id}
            sourceId={String(source.id)}
            sourceType={source.definition.type}
          />
        )}
        <FloatingActionBar>
          <PermissionedButton
            permission={{
              v2: {
                resource: "source",
                grant: "can_update",
                id: source!.id,
              },
            }}
            variant="primary"
            size="lg"
            isDisabled={!dirty || !complete}
            isLoading={isSaving}
            onClick={save}
          >
            Save changes
          </PermissionedButton>
        </FloatingActionBar>
      </Column>
      <Dialog
        isOpen={testConnectionModalOpen}
        variant="form"
        width="2xl"
        title={`Test connection to ${source!.definition?.name}`}
        actions={
          <ButtonGroup>
            <Button
              variant={
                testResults?.success !== false && !dirty
                  ? undefined
                  : "secondary"
              }
              onClick={() => {
                setTestConnectionModalOpen(false);
              }}
            >
              Close
            </Button>
            {testResults?.success === false ? (
              <Button icon={RefreshIcon} onClick={() => runTest(variables)}>
                Test again
              </Button>
            ) : dirty ? (
              <Button
                isDisabled={!testResults?.success}
                isLoading={updateLoading}
                onClick={async () => {
                  await save();
                  setTestConnectionModalOpen(false);
                }}
              >
                Save changes
              </Button>
            ) : null}
          </ButtonGroup>
        }
        onClose={() => {
          setTestConnectionModalOpen(false);
        }}
      >
        <Testing
          config={config}
          credentialId={credentialId}
          isSetup={false}
          plannerDatabase={plannerDatabase}
          results={testResults}
          schema={schema}
          sourceDefinition={source!.definition}
          steps={testSteps}
          timeElapsed={timeElapsed}
        />
      </Dialog>
    </>
  );
};
